import { Icon } from '@amzn/awsui-components-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocale } from '../../../i18n/useLocale';
import { type ModelChoices, hasChoice, modelLabel } from './models';

interface ModelSelectProps {
  choices: ModelChoices | null;
  selected: string | null;
  onSelect: (key: string) => void;
  disabled?: boolean;
}

/**
 * Model picker for the chat toolbar: a borderless text button like the toolbar's
 * attach / context controls, opening a small menu above it. Renders nothing
 * without a real choice.
 */
export default function ModelSelect({ choices, selected, onSelect, disabled }: ModelSelectProps) {
  const { t } = useTranslation('chat');
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!hasChoice(choices)) return null;
  const current = selected ?? choices.default;
  const currentOption = choices.options.find((o) => o.key === current) ?? choices.options[0];

  const openMenu = () => {
    setActive(Math.max(0, choices.options.findIndex((o) => o.key === current)));
    setOpen(true);
  };
  const pick = (key: string) => {
    setOpen(false);
    buttonRef.current?.focus();
    if (key !== current) onSelect(key);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % choices.options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + choices.options.length) % choices.options.length);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(choices.options[active].key);
    }
  };

  return (
    <div className="chat-panel__model" ref={rootRef} onKeyDown={onKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        // Focus stays on this control while arrowing through the menu, so it is the
        // combobox and carries aria-activedescendant (a plain button may not).
        role="combobox"
        className={`chat-panel__toolbar-model${open ? ' chat-panel__toolbar-model--open' : ''}`}
        aria-label={`${t('input.model')}: ${modelLabel(currentOption, locale)}`}
        title={t('input.model')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        onClick={() => (open ? setOpen(false) : openMenu())}
        disabled={disabled}
      >
        <Icon name="gen-ai" size="small" />
        <span className="chat-panel__toolbar-model-name">{modelLabel(currentOption, locale)}</span>
        <Icon name={open ? 'caret-up-filled' : 'caret-down-filled'} size="small" />
      </button>
      {open && (
        <ul
          id={listId}
          className="chat-panel__model-menu"
          role="listbox"
          aria-label={t('input.model')}
        >
          {choices.options.map((o, i) => (
            <li
              key={o.key}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={o.key === current}
              className={`chat-panel__model-option${i === active ? ' chat-panel__model-option--active' : ''}${
                o.key === current ? ' chat-panel__model-option--selected' : ''
              }`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o.key)}
            >
              <span>{modelLabel(o, locale)}</span>
              {o.key === current && <Icon name="check" size="small" variant="link" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
